import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardIcon, CheckIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { fadeIn } from '../../../lib/motion';
import ModuleViewContainer from './ModuleViewContainer';

export interface CodeFileItem {
  /** Inline source (authoring / preview path). */
  code?: string;
  language?: string;
  filename?: string;
  /** Uploaded file (learner runtime path). */
  file_url?: string;
  title?: string;
}

export interface CodeFileViewProps {
  item: CodeFileItem;
  mode: 'learner' | 'preview';
}

/**
 * Renders a `source_code_file` content module — the one content type that
 * previously had no view (ModulePlayer fell through to a "not available"
 * message). Shows the inline `code` (authoring/preview details carry it) with a
 * language chip + copy button; falls back to a download affordance when only an
 * uploaded `file_url` is present (the learner-sanitized shape).
 */
const CopyButton: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort — clipboard may be unavailable (insecure context)
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 active:scale-[0.97] transition-[color,background-color,transform] duration-150"
      aria-label={copied ? 'Copied' : 'Copy code'}
    >
      {copied ? (
        <>
          <CheckIcon className="w-3.5 h-3.5" /> Copied
        </>
      ) : (
        <>
          <ClipboardIcon className="w-3.5 h-3.5" /> Copy
        </>
      )}
    </button>
  );
};

const CodeBlock: React.FC<{ item: CodeFileItem }> = ({ item }) => {
  if (typeof item.code === 'string' && item.code.length > 0) {
    return (
      <div className="rounded-2xl overflow-hidden border border-border-secondary bg-neutral-900 shadow-[0_4px_10px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10">
          <span className="text-xs font-mono font-semibold text-neutral-400 truncate">
            {item.filename || item.language || 'source'}
          </span>
          <CopyButton code={item.code} />
        </div>
        <pre className="p-4 overflow-x-auto text-sm leading-relaxed text-neutral-100 scrollbar-thin">
          <code>{item.code}</code>
        </pre>
      </div>
    );
  }

  if (item.file_url) {
    return (
      <a
        href={item.file_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-primary-700 dark:text-primary-200 bg-primary-50/80 dark:bg-primary-900/25 border border-primary-200/70 dark:border-primary-800 hover:brightness-105 active:scale-[0.98] transition-[filter,transform] duration-150"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
        {item.filename || 'Download source file'}
      </a>
    );
  }

  return <p className="text-sm text-text-secondary italic">No code provided.</p>;
};

const CodeFileView: React.FC<CodeFileViewProps> = ({ item, mode }) => {
  if (mode === 'learner') {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer>
          {item.title && (
            <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight mb-4">
              {item.title}
            </h2>
          )}
          <CodeBlock item={item} />
        </ModuleViewContainer>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="w-full">
      <CodeBlock item={item} />
    </motion.div>
  );
};

export default CodeFileView;
